import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IComplaint } from "../../exports/interfaces";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import Select from "react-select";
import { Upload } from "lucide-react";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useLoader } from "../../context/LoaderContext";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { useOption } from "../../context/OptionContext";
import {
  convertNepaliToEnglishDate,
  formatDateBySetting,
  renderExistingFilePreview,
  renderFilePreview,
  validateFileSize,
} from "../../exports/functions";
import NepaliCalendar from "../NepaliCalendar";
import useMobile from "../../hooks/useMobile";
import { useSettings } from "../../context/SettingsContext";
import { isValidEmail } from "../register/registerValidation";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

const SelectWithError = Select as unknown as React.ComponentType<any>;

const ComplaintsDetails = ({ complaint }: { complaint: IComplaint | null }) => {
  const { setLoading } = useLoader();
  const { categories } = useOption();
  const { generalSettings } = useSettings();
  const { user } = useUser();
  const { sendNotification } = useNotification();
  const isMobile = useMobile();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [formData, setFormData] = useState<IComplaint | null>(null);
  const [errors, setErrors] = useState<{
    complainantName: string;
    complainantContact: string;
    email: string;
    complaintType: string;
    complaintPriority: string;
    complaintArea: string;
    cooperativeName: string;
    cooperativeRegistrationNumber: string;
    complainDetails: string;
    filedOn: string;
  }>({
    complainantName: "",
    complainantContact: "",
    email: "",
    complaintType: "",
    complaintPriority: "",
    complaintArea: "",
    cooperativeName: "",
    cooperativeRegistrationNumber: "",
    complainDetails: "",
    filedOn: "",
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!validateFileSize(file)) {
      e.target.value = "";
      return;
    }

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            fileName: file.name,
            file,
          }
        : prev,
    );
  };

  useEffect(() => {
    if (complaint) {
      setFormData(complaint);
    }
  }, [complaint]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            [name]: value,
          }
        : prev,
    );
    // setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getComplaintErrors = (data: IComplaint | null) => {
    const validationErrors = {
      complainantName: "",
      complainantContact: "",
      email: "",
      complaintType: "",
      complaintPriority: "",
      complaintArea: "",
      cooperativeName: "",
      cooperativeRegistrationNumber: "",
      complainDetails: "",
      filedOn: "",
    };

    if (!data?.complainantName?.trim()) {
      validationErrors.complainantName = "Complainant Name is required.";
    }

    if (!data?.complainantContact?.trim()) {
      validationErrors.complainantContact = "Mobile Number is required.";
    } else if (!/^\d{10}$/.test(data.complainantContact.trim())) {
      validationErrors.complainantContact = "Exactly 10 digits required.";
    }

    if (!data?.email?.trim()) {
      validationErrors.email = "Email Address is required.";
    } else if (!isValidEmail(data.email)) {
      validationErrors.email = "Invalid Email format.";
    }

    if (!data?.complaintType?.trim()) {
      validationErrors.complaintType = "Complaint Type is required.";
    }

    if (!data?.complaintPriority?.trim()) {
      validationErrors.complaintPriority = "Complaint Priority is required.";
    }

    if (!data?.complaintArea?.trim()) {
      validationErrors.complaintArea = "Complaint Area is required.";
    }

    if (!data?.cooperativeName?.trim()) {
      validationErrors.cooperativeName = "Cooperative Name is required.";
    }

    if (
      !data?.cooperativeRegistrationNumber ||
      String(data.cooperativeRegistrationNumber).trim() === ""
    ) {
      validationErrors.cooperativeRegistrationNumber =
        "Cooperative Registration Number is required.";
    } else if (Number.isNaN(Number(data.cooperativeRegistrationNumber))) {
      validationErrors.cooperativeRegistrationNumber =
        "Registration Number must be numeric.";
    }

    // if (!data?.complainDetails?.trim()) {
    //   validationErrors.complainDetails = "Complaint details are required.";
    // }

    if (!data?.filedOn?.trim()) {
      validationErrors.filedOn = "Received Date is required.";
    }

    return validationErrors;
  };

  const scrollToFirstError = (validationErrors: Record<string, string>) => {
    const fieldOrder = [
      "complainantName",
      "complainantContact",
      "email",
      "complaintType",
      "complaintPriority",
      "complaintArea",
      "cooperativeName",
      "cooperativeRegistrationNumber",
      "complainDetails",
      "filedOn",
    ];

    const firstErrorField = fieldOrder.find((field) => validationErrors[field]);

    if (!firstErrorField) return;

    let element: HTMLElement | null = null;

    if (firstErrorField === "complaintType") {
      element = document.querySelector(
        '[data-field="complaintType"]',
      ) as HTMLElement | null;
    } else if (firstErrorField === "complaintPriority") {
      element = document.querySelector(
        '[data-field="complaintPriority"]',
      ) as HTMLElement | null;
    } else if (firstErrorField === "complaintArea") {
      element = document.querySelector(
        '[data-field="complaintArea"]',
      ) as HTMLElement | null;
    } else if (firstErrorField === "filedOn") {
      element = document.querySelector(
        '[data-field="filedOn"]',
      ) as HTMLElement | null;
    } else {
      element = document.querySelector(
        `[name="${firstErrorField}"]`,
      ) as HTMLElement | null;
    }

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // element.focus?.();
    }
  };

  const complaintTypeOptions =
    categories
      ?.find((item) => item.name === "Complaint Type")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const priorityOptions =
    categories
      ?.find((item) => item.name === "Complaint Priority")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const cooperativeTypeOptions = [
    { value: "All Cooperative", label: "All Cooperative" },
    { value: "Agricultural Cooperative", label: "Agricultural Cooperative" },
    {
      value: "Saving and Credit Cooperative",
      label: "Saving and Credit Cooperative",
    },
    { value: "Multipurpose Cooperative", label: "Multipurpose Cooperative" },
    { value: "Other Cooperative", label: "Other Cooperative" },
  ];

  const handleSelectChange = (
    name: keyof IComplaint,
    selected: SelectOption | null,
  ) => {
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            [name]: selected?.value,
          }
        : prev,
    );
  };

  const handleUpdate = async () => {
    if (!formData) return;

    const validationErrors = getComplaintErrors(formData);
    setErrors(validationErrors);

    if (Object.values(validationErrors).some((error) => error !== "")) {
      scrollToFirstError(validationErrors);
      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();

      payload.append("complainantName", formData.complainantName || "");
      payload.append("complainantContact", formData.complainantContact || "");
      payload.append("email", formData.email || "");

      payload.append("complaintType", formData.complaintType || "");
      payload.append("complaintPriority", formData.complaintPriority || "");
      payload.append("complaintArea", formData.complaintArea || "");

      payload.append("cooperativeName", formData.cooperativeName || "");
      payload.append(
        "cooperativeRegistrationNumber",
        formData.cooperativeRegistrationNumber || "",
      );

      payload.append("complainDetails", formData.complainDetails || "");
      payload.append("filedOn", formData.filedOn || "");

      if (formData.file) {
        payload.append("documentUrl", formData.file);
      }

      const response = await axiosClient.put(
        `/complaints/${formData.id}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 200 || response.status === 201) {
        // Send notification
        sendNotification(`Complaint updated`, user?.user?.userName || "User");
        setTimeout(() => {
          setLoading(false);
          dynamicSuccess("Complaint updated successfully!");
        }, 1000);
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Edit Complaint</h3>
          <small>Edit complaint from a cooperative member or stakeholder</small>
        </div>
      </div>

      <div className="dashboard-card p-3 mt-3">
        <h5 className="text-uppercase mb-4">Complainant Information</h5>
        <div className="row g-3">
          <Input
            type="text"
            label="Complainant Name"
            name="complainantName"
            value={formData?.complainantName}
            placeholder="Enter Complainant Name"
            col={6}
            onChange={handleChange}
            error={errors.complainantName}
          />

          <Input
            type="text"
            label="Mobile Number"
            name="complainantContact"
            value={formData?.complainantContact}
            placeholder="Enter Mobile Number"
            col={6}
            onChange={(e) => {
              if (/^[0-9]*$/.test(e.target.value)) {
                handleChange(e);
              }
            }}
            error={errors.complainantContact}
            pattern="\d*"
            maxLength={10}
          />

          <Input
            type="email"
            label="Email Address"
            name="email"
            value={formData?.email}
            placeholder="Enter Email Address"
            col={6}
            onChange={handleChange}
            error={errors.email}
          />
        </div>
      </div>

      <div className="dashboard-card p-3 mt-3" style={{ overflow: "unset" }}>
        <h5 className="text-uppercase mb-4">Complaint Details</h5>

        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">
              Complaint Type <span className="primary-color">*</span>
            </label>
            <div data-field="complaintType">
              <SelectWithError
                styles={customSelectStyles}
                options={complaintTypeOptions}
                isError={Boolean(errors.complaintType)}
                placeholder="Select Complaint Type"
                value={
                  complaintTypeOptions.find(
                    (option) => option.value === formData?.complaintType,
                  ) || null
                }
                onChange={(selected: SelectOption | null) =>
                  handleSelectChange("complaintType", selected)
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            {errors.complaintType && (
              <p className="error-message">{errors.complaintType}</p>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">
              Complaint Priority <span className="primary-color">*</span>
            </label>
            <div data-field="complaintPriority">
              <SelectWithError
                styles={customSelectStyles}
                options={priorityOptions}
                isError={Boolean(errors.complaintPriority)}
                placeholder="Select Complaint Priority"
                value={
                  priorityOptions.find(
                    (option) => option.value === formData?.complaintPriority,
                  ) || null
                }
                onChange={(selected: SelectOption | null) =>
                  handleSelectChange("complaintPriority", selected)
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            {errors.complaintPriority && (
              <p className="error-message">{errors.complaintPriority}</p>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">
              Complaint Area <span className="primary-color">*</span>
            </label>
            <div data-field="complaintArea">
              <SelectWithError
                styles={customSelectStyles}
                options={cooperativeTypeOptions}
                isError={Boolean(errors.complaintArea)}
                placeholder="Select Complaint Area"
                value={
                  cooperativeTypeOptions.find(
                    (option) => option.value === formData?.complaintArea,
                  ) || null
                }
                onChange={(selected: SelectOption | null) =>
                  handleSelectChange("complaintArea", selected)
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            {errors.complaintArea && (
              <p className="error-message">{errors.complaintArea}</p>
            )}
          </div>
          <Input
            type="text"
            label="Cooperative Name"
            name="cooperativeName"
            value={formData?.cooperativeName}
            placeholder="Enter Cooperative Name"
            col={4}
            onChange={handleChange}
            error={errors.cooperativeName}
          />

          <Input
            type="text"
            label="Cooperative Registration Number"
            name="cooperativeRegistrationNumber"
            value={formData?.cooperativeRegistrationNumber}
            placeholder="Enter Cooperative Registration Number"
            col={4}
            onChange={(e) => {
              if (/^[0-9-]*$/.test(e.target.value)) {
                handleChange(e);
              }
            }}
            error={errors.cooperativeRegistrationNumber}
            pattern="\d*"
            maxLength={10}
          />

          <div className="col-md-4" data-field="filedOn">
            <label className="form-label">Received Date</label>
            <div className="dropdown position-relative" ref={dropdownRef}>
              <button
                type="button"
                className={`form-control text-start nepali-date-picker ${
                  errors.filedOn ? "input-error" : ""
                }`}
                onClick={() => setShowCalendar(!showCalendar)}
              >
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span className="text-truncate">
                    {formatDateBySetting(
                      formData?.filedOn,
                      generalSettings?.dateFormat,
                    ) ||
                      generalSettings?.dateFormat ||
                      "mm/dd/yyyy"}
                  </span>

                  <i className="fa-regular fa-calendar"></i>
                </div>
              </button>

              {showCalendar && (
                <div
                  className="dropdown-menu dropdown-menu-end show p-0"
                  style={{
                    width: isMobile ? "249px" : "560px",
                    height: isMobile ? "407px" : "380px",
                    border: "1px solid #ddd",
                    zIndex: 1050,
                    right: 0,
                    left: "auto",
                  }}
                >
                  <NepaliCalendar
                    onDateSelect={(nepaliDate, englishDate) => {
                      const convertedEnglishDate = convertNepaliToEnglishDate(
                        nepaliDate,
                        englishDate,
                      );
                      setFormData((prev) =>
                        prev
                          ? { ...prev, filedOn: convertedEnglishDate }
                          : prev,
                      );
                      setErrors((prev) => ({ ...prev, filedOn: "" }));
                      setShowCalendar(false);
                    }}
                  />
                </div>
              )}
            </div>
            {errors.filedOn && (
              <p className="error-message">{errors.filedOn}</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">Complaint Details</label>
            <textarea
              className={`form-control ${errors.complainDetails ? "input-error" : ""}`}
              rows={4}
              value={formData?.complainDetails}
              name="complainDetails"
              placeholder="Enter Complaint Details"
              onChange={(e) => {
                setFormData((prev) =>
                  prev ? { ...prev, complainDetails: e.target.value } : prev,
                );
                setErrors((prev) => ({ ...prev, complainDetails: "" }));
              }}
            />
            {errors.complainDetails && (
              <p className="error-message">{errors.complainDetails}</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">Complaint (PDF, JPEG, PNG)</label>

            <div
              className={`upload-box p-4 ${!formData?.file && !formData?.documentUrl && "text-center"}`}
            >
              {formData?.file ? (
                renderFilePreview(formData.file, formData.fileName, (e: any) =>
                  handleFileChange(e),
                )
              ) : formData?.documentUrl ? (
                renderExistingFilePreview(
                  formData.documentUrl,
                  handleFileChange,
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e);
                      }}
                    />
                  </label>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="text-end mt-3">
          <button
            className="btn btn-badge-border px-3 me-2"
            onClick={() => navigate("/complaints-list")}
          >
            Cancel
          </button>
          <button className="btn btn-badge-primary px-3" onClick={handleUpdate}>
            Update
          </button>
        </div>
      </div>

      {/* <div className="text-end mt-3">
        <button
          className="btn btn-badge-primary px-3"
          onClick={() => navigate("/complaints-list")}
        >
          Back to list
        </button>
      </div> */}
    </>
  );
};

const Input = ({
  type,
  label,
  name,
  value,
  placeholder,
  col,
  onChange,
  disabled,
  error,
  pattern,
  maxLength,
}: {
  type: string;
  label: React.ReactNode;
  name: string;
  value?: string | number;
  placeholder?: string;
  col: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string;
  pattern?: string;
  maxLength?: number;
}) => (
  <div className={`col-md-${col}`}>
    <label className="form-label">
      {label} <span className="primary-color">*</span>
    </label>
    <input
      type={type}
      name={name}
      className={`form-control ${error ? "input-error" : ""}`}
      value={value || ""}
      placeholder={placeholder}
      onChange={onChange}
      disabled={disabled}
      pattern={pattern}
      maxLength={maxLength}
    />
    {error && <p className="error-message">{error}</p>}
  </div>
);

export default ComplaintsDetails;
